// ============================================================
// 天水數位店長 — 主管簽核儀表板
// 出勤異常 + 請假申請，批次簽核
// ============================================================

import React, { useEffect, useState, useMemo } from 'react';
import { useAttendanceStore, AttendanceRecord } from '../stores/attendanceStore';
import { api } from '../lib/api';
import { formatTaipeiTime } from '../lib/datetime';

interface PendingLeave {
  id: string;
  userId: string;
  user?: { id: string; name: string; avatar?: string };
  leaveType: string;
  startDate: string;
  endDate: string;
  hours: number;
  reason?: string;
  status: string;
}

interface PendingOvertime {
  id: string;
  userId: string;
  user?: { id: string; name: string; avatar?: string; payType?: string };
  clockIn: string | null;
  clockOut: string | null;
  overtimeMinutes: number;
  schedule?: { startTime: string; endTime: string; date: string } | null;
}

type TabType = 'attendance' | 'leave' | 'overtime';

const typeConfig: Record<string, { label: string; color: string; bg: string }> = {
  LATE: { label: '遲到', color: 'text-rose-500', bg: 'bg-rose-100' },
  EARLY_LEAVE: { label: '早退', color: 'text-rose-400', bg: 'bg-rose-50' },
  MISSED: { label: '忘打卡', color: 'text-mist-500', bg: 'bg-mist-100' },
  PENDING: { label: '待確認', color: 'text-smoke-400', bg: 'bg-smoke-100' },
};

const leaveTypeLabels: Record<string, string> = {
  ANNUAL: '特休', SICK: '病假', PERSONAL: '事假', OFFICIAL: '公假',
  MENSTRUAL: '生理假', FAMILY_CARE: '家庭照顧假', MATERNITY: '產假',
  PATERNITY: '陪產假', UNPAID: '無薪假', BEREAVEMENT: '喪假',
};

export function ApprovalDashboard() {
  const { pendingRecords, fetchPending, approve } = useAttendanceStore();
  const [pendingLeaves, setPendingLeaves] = useState<PendingLeave[]>([]);
  const [pendingOvertime, setPendingOvertime] = useState<PendingOvertime[]>([]);
  const [tab, setTab] = useState<TabType>('attendance');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPending();
    fetchPendingLeaves();
    fetchPendingOvertime();
  }, [fetchPending]);

  const fetchPendingLeaves = async () => {
    try {
      const data = await api.get<{ leaves: PendingLeave[] }>('/leaves?status=PENDING');
      setPendingLeaves(data.leaves);
    } catch { /* ignore */ }
  };

  const fetchPendingOvertime = async () => {
    try {
      const data = await api.get<{ records: PendingOvertime[] }>('/attendance/overtime-pending');
      setPendingOvertime(data.records);
    } catch { /* ignore */ }
  };

  const counts = {
    attendance: pendingRecords.length,
    leave: pendingLeaves.length,
    overtime: pendingOvertime.length,
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchAction = async (action: 'APPROVED' | 'REJECTED') => {
    setProcessing(true);
    try {
      for (const id of selectedIds) {
        if (tab === 'attendance') {
          await approve(id, action);
        } else if (tab === 'leave') {
          await api.put(`/leaves/${id}/approve`, { action });
        } else {
          await api.put(`/attendance/${id}/overtime`, { action });
        }
      }
      setSelectedIds(new Set());
      if (tab === 'attendance') fetchPending();
      else if (tab === 'leave') fetchPendingLeaves();
      else fetchPendingOvertime();
    } finally {
      setProcessing(false);
    }
  };

  const handleSingleAction = async (id: string, action: 'APPROVED' | 'REJECTED') => {
    setProcessing(true);
    try {
      if (tab === 'attendance') {
        await approve(id, action);
      } else if (tab === 'leave') {
        await api.put(`/leaves/${id}/approve`, { action });
        setPendingLeaves((prev) => prev.filter((l) => l.id !== id));
      } else {
        await api.put(`/attendance/${id}/overtime`, { action });
        setPendingOvertime((prev) => prev.filter((o) => o.id !== id));
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="h-page flex flex-col bg-morandi-50 p-4">
      <header className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-morandi-800 tracking-tight">簽核中心</h1>
        <div className="text-sm text-morandi-400">共 {counts.attendance + counts.leave + counts.overtime} 筆待處理</div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 bg-morandi-100 rounded-btn p-1 mb-4 w-fit flex-wrap">
        <button
          onClick={() => { setTab('attendance'); setSelectedIds(new Set()); }}
          className={`px-4 py-2 rounded-btn text-sm font-medium transition-all min-h-touch
            ${tab === 'attendance' ? 'bg-white shadow-card text-morandi-800' : 'text-morandi-400 hover:text-morandi-600'}`}
        >
          出勤異常
          {counts.attendance > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs bg-rose-400 text-white rounded-full">{counts.attendance}</span>
          )}
        </button>
        <button
          onClick={() => { setTab('overtime'); setSelectedIds(new Set()); }}
          className={`px-4 py-2 rounded-btn text-sm font-medium transition-all min-h-touch
            ${tab === 'overtime' ? 'bg-white shadow-card text-morandi-800' : 'text-morandi-400 hover:text-morandi-600'}`}
        >
          加班核准
          {counts.overtime > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs bg-amber-500 text-white rounded-full">{counts.overtime}</span>
          )}
        </button>
        <button
          onClick={() => { setTab('leave'); setSelectedIds(new Set()); }}
          className={`px-4 py-2 rounded-btn text-sm font-medium transition-all min-h-touch
            ${tab === 'leave' ? 'bg-white shadow-card text-morandi-800' : 'text-morandi-400 hover:text-morandi-600'}`}
        >
          請假申請
          {counts.leave > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs bg-smoke-400 text-white rounded-full">{counts.leave}</span>
          )}
        </button>
      </div>

      {/* Batch Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-2 animate-slide-up">
          <span className="text-sm text-morandi-500">已選 {selectedIds.size} 筆</span>
          <button onClick={() => handleBatchAction('APPROVED')} disabled={processing}
            className="px-4 py-2 bg-sage-500 text-white rounded-btn text-sm font-medium hover:bg-sage-600 transition-colors min-h-touch">
            批次核准
          </button>
          <button onClick={() => handleBatchAction('REJECTED')} disabled={processing}
            className="px-4 py-2 bg-rose-400 text-white rounded-btn text-sm font-medium hover:bg-rose-500 transition-colors min-h-touch">
            批次駁回
          </button>
        </div>
      )}

      {/* List */}
      <div className="flex-1 space-y-2 overflow-auto scrollbar-hide">
        {tab === 'attendance' && pendingRecords.map((item) => {
          const cfg = typeConfig[item.status] ?? typeConfig.PENDING;
          return (
            <div key={item.id} onClick={() => toggleSelect(item.id)}
              className={`flex items-center gap-4 bg-white rounded-card p-4 shadow-card cursor-pointer transition-all active:scale-[0.99]
                ${selectedIds.has(item.id) ? 'ring-2 ring-smoke-300 bg-smoke-50' : 'hover:shadow-card-lg'}`}>
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors
                ${selectedIds.has(item.id) ? 'bg-smoke-500 border-smoke-500' : 'border-morandi-300'}`}>
                {selectedIds.has(item.id) && <span className="text-white text-xs font-bold">✓</span>}
              </div>
              <div className="w-10 h-10 rounded-full bg-earth-200 flex items-center justify-center text-earth-600 font-medium flex-shrink-0">
                {(item.user?.name ?? '?').slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-morandi-700">{item.user?.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-chip ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                </div>
                <div className="text-xs text-morandi-400 mt-1">
                  {item.clockIn ? new Date(item.clockIn).toLocaleString('zh-TW') : ''}
                  {item.schedule ? ` | 排班 ${item.schedule.startTime}-${item.schedule.endTime}` : ''}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={(e) => { e.stopPropagation(); handleSingleAction(item.id, 'APPROVED'); }}
                  className="px-3 py-2 bg-sage-100 text-sage-600 rounded-btn text-sm font-medium hover:bg-sage-200 transition-colors min-h-touch">核准</button>
                <button onClick={(e) => { e.stopPropagation(); handleSingleAction(item.id, 'REJECTED'); }}
                  className="px-3 py-2 bg-rose-100 text-rose-500 rounded-btn text-sm font-medium hover:bg-rose-200 transition-colors min-h-touch">駁回</button>
              </div>
            </div>
          );
        })}

        {tab === 'leave' && pendingLeaves.map((item) => (
          <div key={item.id} onClick={() => toggleSelect(item.id)}
            className={`flex items-center gap-4 bg-white rounded-card p-4 shadow-card cursor-pointer transition-all active:scale-[0.99]
              ${selectedIds.has(item.id) ? 'ring-2 ring-smoke-300 bg-smoke-50' : 'hover:shadow-card-lg'}`}>
            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors
              ${selectedIds.has(item.id) ? 'bg-smoke-500 border-smoke-500' : 'border-morandi-300'}`}>
              {selectedIds.has(item.id) && <span className="text-white text-xs font-bold">✓</span>}
            </div>
            <div className="w-10 h-10 rounded-full bg-earth-200 flex items-center justify-center text-earth-600 font-medium flex-shrink-0">
              {(item.user?.name ?? '?').slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-morandi-700">{item.user?.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-chip bg-smoke-100 text-smoke-500">
                  {leaveTypeLabels[item.leaveType] ?? item.leaveType}
                </span>
              </div>
              <div className="text-xs text-morandi-400 mt-1">
                {item.startDate.slice(0, 10)}{item.startDate !== item.endDate ? ` ~ ${item.endDate.slice(0, 10)}` : ''} | {item.hours} 小時
              </div>
              {item.reason && <div className="text-xs text-morandi-300 mt-0.5">{item.reason}</div>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={(e) => { e.stopPropagation(); handleSingleAction(item.id, 'APPROVED'); }}
                className="px-3 py-2 bg-sage-100 text-sage-600 rounded-btn text-sm font-medium hover:bg-sage-200 transition-colors min-h-touch">核准</button>
              <button onClick={(e) => { e.stopPropagation(); handleSingleAction(item.id, 'REJECTED'); }}
                className="px-3 py-2 bg-rose-100 text-rose-500 rounded-btn text-sm font-medium hover:bg-rose-200 transition-colors min-h-touch">駁回</button>
            </div>
          </div>
        ))}

        {tab === 'overtime' && pendingOvertime.map((item) => {
          const fmtTime = (iso: string | null) => (iso ? formatTaipeiTime(iso) : '—');
          const dateStr = item.clockIn ? new Date(item.clockIn).toLocaleDateString('zh-TW') : '—';
          return (
            <div key={item.id} onClick={() => toggleSelect(item.id)}
              className={`flex items-center gap-4 bg-white rounded-card p-4 shadow-card cursor-pointer transition-all active:scale-[0.99]
                ${selectedIds.has(item.id) ? 'ring-2 ring-amber-300 bg-amber-50' : 'hover:shadow-card-lg'}`}>
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors
                ${selectedIds.has(item.id) ? 'bg-amber-500 border-amber-500' : 'border-morandi-300'}`}>
                {selectedIds.has(item.id) && <span className="text-white text-xs font-bold">✓</span>}
              </div>
              <div className="w-10 h-10 rounded-full bg-earth-200 flex items-center justify-center text-earth-600 font-medium flex-shrink-0">
                {(item.user?.name ?? '?').slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-morandi-700">{item.user?.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-chip bg-amber-100 text-amber-700">
                    加班 {item.overtimeMinutes} 分鐘
                  </span>
                  {item.user?.payType === 'MONTHLY' && (
                    <span className="text-xs text-morandi-400">月薪 ×1.34</span>
                  )}
                </div>
                <div className="text-xs text-morandi-400 mt-1">
                  {dateStr}
                  {item.schedule ? ` | 排班 ${item.schedule.startTime}-${item.schedule.endTime}` : ''}
                  {item.clockOut && ` | 實際下班 ${fmtTime(item.clockOut)}`}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={(e) => { e.stopPropagation(); handleSingleAction(item.id, 'APPROVED'); }}
                  className="px-3 py-2 bg-sage-100 text-sage-600 rounded-btn text-sm font-medium hover:bg-sage-200 transition-colors min-h-touch">核准</button>
                <button onClick={(e) => { e.stopPropagation(); handleSingleAction(item.id, 'REJECTED'); }}
                  className="px-3 py-2 bg-rose-100 text-rose-500 rounded-btn text-sm font-medium hover:bg-rose-200 transition-colors min-h-touch">拒絕</button>
              </div>
            </div>
          );
        })}

        {((tab === 'attendance' && pendingRecords.length === 0)
          || (tab === 'leave' && pendingLeaves.length === 0)
          || (tab === 'overtime' && pendingOvertime.length === 0)) && (
          <div className="flex flex-col items-center justify-center py-20 text-morandi-300">
            <div className="text-4xl mb-3">✓</div>
            <div className="text-lg">全部處理完畢</div>
            <div className="text-sm mt-1">目前沒有待簽核的項目</div>
          </div>
        )}
      </div>
    </div>
  );
}
